import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link, useParams } from 'react-router-dom';
import { storeCurrentStep, getStoredStepWithServerFallback, storeVisitStatus, getStoredVisitStatusWithServerFallback, clearSessionState, getServerSessionState, storeSessionState, getStoredStep, getStoredVisitStatus } from './utils/sessionStorage';
import YearOfBirthVerification from './components/YearOfBirthVerification';
import DeviceCheck from './components/DeviceCheck';
import IntakeForms from './components/IntakeForms';
import InsuranceVerification from './components/InsuranceVerification';
import CheckInComplete from './components/checkinComplete/CheckinComplete';
import CheckInUrlHandler from './components/CheckInUrlHandler';
import useCheckIn from './components/redux/hooks/useCheckIn';

// Import the updated token utility functions
import { getTokensFromStorage, storeTokens, decodePinJwt } from './utils/token';
import api from './services/api';
import { useUserData } from './components/redux/hooks/useUserData';

const CheckInFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Helper function to get the MiraSol_CaseID (UUID format required by backend)
  const getMirasolCaseId = (tokenData) => {
    if (!tokenData) return null;
    return tokenData.case_info?.MiraSol_CaseID;
  };
  const { '*': path } = useParams(); // Get the splat path parameter from the route

  // Get step from URL query parameter
  const getStepFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const step = parseInt(params.get('step'), 10);
    return isNaN(step) ? 0 : Math.min(Math.max(0, step), 4); // Ensure step is between 0 and 4
  };

  // Get currentStep from Redux instead of local state
  const { currentStep, updateCurrentStep } = useCheckIn();  
  // Get user data functions from Redux
  const { updateTokenData, resetTokenData } = useUserData();
  
  // Initialize currentStep from URL on component mount
  useEffect(() => {
    const urlStep = getStepFromUrl();
    if (urlStep !== currentStep) {
      updateCurrentStep(urlStep);
    }
  }, []);
  const [tokenData, setTokenData] = useState(null); // Stores the decoded JWT payload
  const [tokenError, setTokenError] = useState('');
  const [checkInData, setCheckInData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [deviceCheckFailed, setDeviceCheckFailed] = useState(false); // New state to track device check failure
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [storedStepNumber, setStoredStepNumber] = useState(null);
  const [shortUrl, setShortUrl] = useState(null); // To store the short URL from the initial link


  // Steps in the check-in process
  const steps = [
    { name: 'Verify Identity', component: YearOfBirthVerification },
    { name: 'Check Devices', component: DeviceCheck },
    { name: 'Complete Forms', component: IntakeForms },
    { name: 'Verify Insurance', component: InsuranceVerification },
    { name: 'Ready for Visit', component: CheckInComplete }
  ];

  // Track the highest step reached
  const [maxStepReached, setMaxStepReached] = useState(0);

  // Effect to handle initial load and token/short URL processing
  useEffect(() => {
    const initializeFlow = async () => {
      try {
        // On initial load or path change, check for existing tokens in localStorage
        const storedTokens = getTokensFromStorage();
        // console.log('CheckInFlow: Stored tokens:', storedTokens ? 'Found' : 'Not found');

        if (storedTokens && storedTokens.pin_jwt && storedTokens.refresh_token && storedTokens.case_id && storedTokens.state) {
          // Found stored tokens, attempt to decode the JWT to get payload
          // console.log('CheckInFlow: Attempting to decode stored token');
          
          try {
            // IMPORTANT: Properly await the Promise returned by decodePinJwt
            const decodedPayload = await decodePinJwt(storedTokens.pin_jwt);

            if (decodedPayload && !decodedPayload.error) {
              // Token is valid, set token data and mark as returning user
              setTokenData(decodedPayload);
              // Also update Redux store with token data
              updateTokenData(decodedPayload);
              setIsReturningUser(true);

              // Get the MiraSol_CaseID (UUID format) for backend status API calls
              const mirasolCaseId = getMirasolCaseId(decodedPayload);
              
              // Get stored step and visit status with single server call for efficiency
              const serverState = await getServerSessionState(mirasolCaseId);
              let storedStep = null;
              let visitStatus = null;
              
              if (serverState) {
                storedStep = serverState.currentStep;
                visitStatus = serverState.visitStatus;
                // Store locally for performance
                if (storedStep || visitStatus) {
                  storeSessionState(serverState);
                }
              } else {
                // Fallback to localStorage if server doesn't have data
                storedStep = getStoredStep(mirasolCaseId);
                visitStatus = getStoredVisitStatus(mirasolCaseId);
              }
              // console.log(`CheckInFlow: Stored visit status for case ${storedTokens.case_id}:`, visitStatus);
              
              // If the visit status is 'Complete', go directly to the final step (CheckInComplete)
              if (visitStatus === 'Complete') {
                // console.log('CheckInFlow: Visit is already complete, going to CheckInComplete step');
                setStoredStepNumber(4); // CheckInComplete is step 4
                setMaxStepReached(4);
                updateCurrentStep(4);
              }
              // If the stored step is 4 (CheckInComplete), go directly to that step
              else if (storedStep === 4) {
                // console.log('CheckInFlow: Stored step is CheckInComplete, going to that step');
                setStoredStepNumber(4);
                setMaxStepReached(4);
                updateCurrentStep(4);
              }
              // For other stored steps, use them as is, but respect URL step if provided
              else if (storedStep !== null && storedStep > 0) {
                // console.log(`CheckInFlow: Found stored step: ${storedStep} for case: ${storedTokens.case_id}`);
                setStoredStepNumber(storedStep);
                setMaxStepReached(Math.max(storedStep, maxStepReached));
                
                // Check if URL has a specific step parameter
                const urlStep = getStepFromUrl();
                if (urlStep > 0) {
                  updateCurrentStep(urlStep);
                  setMaxStepReached(Math.max(urlStep, maxStepReached));
                } else {
                  updateCurrentStep(storedStep);
                }
              } else {
                // If tokens exist but no stored step, start from step 1 (Device Check)
                // assuming YOB verification was completed to get the token
                // console.log('CheckInFlow: No stored step found, starting from step 1');
                updateCurrentStep(1);
                setMaxStepReached(1);
              }

              setIsLoading(false);
              return; // Stop here, we have tokens and processed them
            } else {
              // console.log("CheckInFlow: Stored token invalid or expired, clearing storage.");
              // Stored token is invalid or expired, clear storage
              localStorage.removeItem('pin_jwt');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('case_id');
              localStorage.removeItem('state');
              // Continue to process URL for a new token
            }
          } catch (error) {
            console.error('CheckInFlow: Error decoding stored token:', error);
            // Continue to process URL for a new token
          }
        }

        // If no valid tokens in storage, check if the path parameter is a short URL code        
        if (path && path.startsWith('p-')) {
          // We found a code in the URL path
          const code = path.substring(2); // Remove 'p-' prefix
          const fullShortUrl = `${window.location.origin}/p-${code}`;
          setShortUrl(fullShortUrl);
          // Stay on step 0 (YearOfBirthVerification) to collect YOB and initiate the new flow
          updateCurrentStep(0);
          setMaxStepReached(0);
        } else {
          // No tokens in storage and not a short URL path - cannot proceed
          setTokenError('That\'s not a valid check-in link. Check your text or email and enter the 6-digit code below.');
        }

        setIsLoading(false);
      } catch (error) {
        setTokenError('An unexpected error occurred. Please try again or contact support.');
        setIsLoading(false);
      }
    };

    initializeFlow();
  }, [path]); // Only depend on 'path' parameter to re-run when the path changes

  // Update maxStepReached when currentStep increases
  useEffect(() => {
    if (currentStep > maxStepReached) {
      setMaxStepReached(currentStep);
    }
  }, [currentStep, maxStepReached]);

  // Update URL when step changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const currentStepParam = params.get('step');
    
    // Only update if the step parameter is different from currentStep
    if (parseInt(currentStepParam, 10) !== currentStep) {
      params.set('step', currentStep.toString());

      // Remove formid and version from URL when not on the forms step (step 2)
      if (currentStep !== 2) {
        params.delete('formid');
        params.delete('version');
      }

      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [currentStep, location.pathname, navigate]);

  // Store current step in localStorage when it changes
  useEffect(() => {
    // Only store step if we have token data (meaning we have a valid session)
    const mirasolCaseId = getMirasolCaseId(tokenData);
    if (tokenData && mirasolCaseId && currentStep > 0) {
      const storeProgress = async () => {
        try {
          await storeCurrentStep(currentStep, mirasolCaseId);
          await storeVisitStatus('inProgress', mirasolCaseId);
        } catch (error) {
          console.error('Failed to store progress:', error);
        }
      };
      storeProgress();
    }
  }, [currentStep, tokenData]);

  // Function to navigate to a specific step
  const goToStep = (stepIndex) => {
    // Only allow going to steps that have been completed or the current step
    if (stepIndex === 2) { // Special handling for Step 2 (Complete Forms)
      if (tokenData && tokenData.form_ids && Array.isArray(tokenData.form_ids)) {
        const eligibleForms = tokenData.form_ids
          .map(item => {
            let processedVersion = item.version;
            if (typeof processedVersion === 'string' && processedVersion.includes('.')) {
              const versionParts = processedVersion.split('.');
              processedVersion = parseInt(versionParts[0], 10);
            }
            return {
              form_id: item.form_id,
              version: processedVersion || null,
              original_version: item.version || null,
              assigned: item.assigned,
              reason: item.reason
            };
          })
          .filter(item => item.assigned === true && item.reason === 'Form assigned successfully.');

        if (eligibleForms.length > 0) {
          const firstForm = eligibleForms[0];
          // console.log('CheckInFlow: Eligible forms found, navigating to Step 2 with first form:', firstForm);
          updateCurrentStep(stepIndex);
          if (stepIndex > maxStepReached) {
            setMaxStepReached(stepIndex);
          }
          // Navigate to Step 2 with the first form's ID and version
          navigate({
            search: `?step=${stepIndex}&formid=${firstForm.form_id}${firstForm.original_version ? `&version=${firstForm.original_version}` : ''}`
          });
        } else {
          // console.log('CheckInFlow: No eligible forms found, cannot navigate to Step 2 with form details.');
          // If no eligible forms, still navigate to step 2 but without form details
          updateCurrentStep(stepIndex);
          if (stepIndex > maxStepReached) {
            setMaxStepReached(stepIndex);
          }
           navigate({
            search: `?step=${stepIndex}`
          });
        }
      } else {
        //  console.warn('CheckInFlow: tokenData or form_ids missing/invalid when navigating to Step 2. Navigating without form details.');
         // tokenData or form_ids missing/invalid, navigate to step 2 without form details
         updateCurrentStep(stepIndex);
          if (stepIndex > maxStepReached) {
            setMaxStepReached(stepIndex);
          }
           navigate({
            search: `?step=${stepIndex}`
          });
      }
    } else { // Default handling for other steps
      updateCurrentStep(stepIndex);
      // Update max step reached if needed
      if (stepIndex > maxStepReached) {
        setMaxStepReached(stepIndex);
      }
      // Default navigation for other steps
      const params = new URLSearchParams(location.search);
      params.set('step', stepIndex.toString());
      // Remove formid and version from URL when not on the forms step (step 2)
      if (stepIndex !== 2) {
        params.delete('formid');
        params.delete('version');
      }
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  };

  // Handle step completion
  // This function will now receive the token data (PIN-JWT payload) from Step 0
  const handleStepComplete = async (stepData = {}) => {
    
    // If step 0 (YOB verification) is completed, stepData will contain the new token info
    if (currentStep === 0 && stepData.pin_jwt && stepData.refresh_token && stepData.state) {
      
      try {
        // Decode the new JWT to get the payload first
        const decodedPayload = await decodePinJwt(stepData.pin_jwt);

        if (decodedPayload && !decodedPayload.error) {
          // Check if this is a different patient than what's currently stored
          const existingTokens = getTokensFromStorage();
          const newCaseId = decodedPayload.case_info?.MiraSol_CaseID; // Only use MiraSol_CaseID (UUID format) for backend compatibility
          
          if (!newCaseId) {
            setTokenError('Seems like we ran into an unexpected issue (error Mira-2414565). Please contact support for assistance.');
            return;
          }
          if (existingTokens && existingTokens.case_id && existingTokens.case_id !== newCaseId) {
            // Clear all existing patient session data for the old patient
            clearSessionState();
          }
          
          // Store the new tokens and state
          storeTokens(stepData.pin_jwt, stepData.refresh_token, stepData.state, newCaseId);
          
          // Store the resolved payload, not the Promise
          // console.log('CheckInFlow: Setting tokenData with decoded payload');
          setTokenData(decodedPayload);
          // Also update Redux store with token data
          updateTokenData(decodedPayload);
          
          // Check for existing progress on the backend before proceeding
          try {
            // Use single API call for efficiency (cached from previous call if recent)
            const serverState = await getServerSessionState(newCaseId);
            let storedStep = null;
            let visitStatus = null;
            
            if (serverState) {
              storedStep = serverState.currentStep;
              visitStatus = serverState.visitStatus;
            } else {
              // Fallback to localStorage if server doesn't have data
              storedStep = getStoredStep(newCaseId);
              visitStatus = getStoredVisitStatus(newCaseId);
            }
            
            let targetStep = 1; // Default to step 1 (Device Check)
            
            // If the visit is already complete, go to final step
            if (visitStatus === 'Complete') {
              targetStep = 4;
            }
            // If there's a stored step, use it
            else if (storedStep !== null && storedStep > 0) {
              targetStep = storedStep;
            }            
            updateCurrentStep(targetStep);
            setMaxStepReached(Math.max(targetStep, maxStepReached));
            
            // Store the current step
            if (newCaseId) {
              try {
                await storeCurrentStep(targetStep, newCaseId);
                await storeVisitStatus(visitStatus || 'inProgress', newCaseId);
              } catch (error) {
                // Silent fail - progress storage is not critical
              }
            }
          } catch (error) {
            // console.error('CheckInFlow: Error checking backend progress, defaulting to step 1:', error);
            // Fallback to step 1 if there's an error
            const nextStep = currentStep + 1;
            updateCurrentStep(nextStep);
            if (nextStep > maxStepReached) {
              setMaxStepReached(nextStep);
            }
            
            // Store the current step (which is now 1)
            if (newCaseId) {
              try {
                await storeCurrentStep(nextStep, newCaseId);
                await storeVisitStatus('inProgress', newCaseId);
              } catch (error) {
                // Silent fail
              }
            }
          }
        } else {
          // Handle error decoding the newly received token
          setTokenError("A technical issue occurred after verification. Please try again or contact support.");
          setIsLoading(false); // Stop loading if there was an error
        }
      } catch (error) {
        setTokenError("A technical issue occurred after verification. Please try again or contact support.");
        setIsLoading(false);
      }
      
      return; // Stop here, handled step 0 completion
    } else if (currentStep === 1) { // Handle completion of Device Check (Step 1)
      // console.log('CheckInFlow: Step 1 (Device Check) completed.');
      // Save data from the completed step (if any)
      setCheckInData(prevData => ({
        ...prevData,
        ...stepData
      }));

      // Decode JWT to find forms and navigate to Step 2 with the first form ID
      if (tokenData && tokenData.form_ids && Array.isArray(tokenData.form_ids)) {
        // console.log('CheckInFlow: Found form_ids in tokenData, filtering eligible forms.');
        const eligibleForms = tokenData.form_ids
          .map(item => {
            // Process version if it's a string like "1.2"
            let processedVersion = item.version;
            if (typeof processedVersion === 'string' && processedVersion.includes('.')) {
              const versionParts = processedVersion.split('.');
              processedVersion = parseInt(versionParts[0], 10);
            }
            return {
              form_id: item.form_id,
              version: processedVersion || null,
              original_version: item.version || null,
              assigned: item.assigned,
              reason: item.reason
            };
          })
          .filter(item => item.assigned === true && item.reason === 'Form assigned successfully.');

        if (eligibleForms.length > 0) {
          const firstForm = eligibleForms[0];
          // console.log('CheckInFlow: Eligible forms found, navigating to Step 2 with first form:', firstForm);
          const nextStep = 2;
          updateCurrentStep(nextStep);
          if (nextStep > maxStepReached) {
            setMaxStepReached(nextStep);
          }
          // Navigate to Step 2 with the first form's ID and version
          navigate({
            search: `?step=${nextStep}&formid=${firstForm.form_id}${firstForm.original_version ? `&version=${firstForm.original_version}` : ''}`
          });
          // Store the current step (which is now 2)
          const mirasolCaseId = getMirasolCaseId(tokenData);
          if (mirasolCaseId) {
            try {
              await storeCurrentStep(nextStep, mirasolCaseId);
              await storeVisitStatus('inProgress', mirasolCaseId);
            } catch (error) {
              // Silent fail
            }
          }
        } else {
          // console.log('CheckInFlow: No eligible forms found, skipping to Step 3.');
          // No eligible forms, skip to Step 3
          const nextStep = 3;
          updateCurrentStep(nextStep);
          if (nextStep > maxStepReached) {
            setMaxStepReached(nextStep);
          }
          // Navigate to Step 3
          navigate({
            search: `?step=${nextStep}`
          });
          // Store the current step (which is now 3)
          const mirasolCaseId = getMirasolCaseId(tokenData);
          if (mirasolCaseId) {
            try {
              await storeCurrentStep(nextStep, mirasolCaseId);
              await storeVisitStatus('inProgress', mirasolCaseId);
            } catch (error) {
              // Silent fail
            }
          }
        }
      } else {
        // console.warn('CheckInFlow: tokenData or form_ids missing/invalid after Step 1 completion. Skipping to Step 3.');
        // tokenData or form_ids missing/invalid, skip to Step 3
        const nextStep = 3;
        updateCurrentStep(nextStep);
        if (nextStep > maxStepReached) {
          setMaxStepReached(nextStep);
        }
        // Navigate to Step 3
        navigate({
          search: `?step=${nextStep}`
        });
        // Store the current step (which is now 3)
        const mirasolCaseId = getMirasolCaseId(tokenData);
        if (mirasolCaseId) {
          try {
            await storeCurrentStep(nextStep, mirasolCaseId);
            await storeVisitStatus('inProgress', mirasolCaseId);
          } catch (error) {
            // Silent fail
          }
        }
      }
      return; // Stop here, handled step 1 completion
    }
    //commented out for now
    // For other steps (Step 2, 3), save data from the completed step and move to the next
    // console.log(`CheckInFlow: Step ${currentStep} completed. Moving to next step.`);
    setCheckInData(prevData => ({
      ...prevData,
      ...stepData
    }));

    const nextStep = currentStep + 1;
    updateCurrentStep(nextStep);

    // Update max step reached if needed
    if (nextStep > maxStepReached) {
      setMaxStepReached(nextStep);
    }

    // If this is the final step, update visit status to complete
    if (nextStep >= steps.length) {
      const mirasolCaseId = getMirasolCaseId(tokenData);
      if (tokenData && mirasolCaseId) {
        try {
          await storeVisitStatus('Complete', mirasolCaseId);
        } catch (error) {
          // Silent fail
        }
      }
    }
  };

  // Render the current step component
  const renderCurrentStep = () => {
    // console.log('CheckInFlow: renderCurrentStep called. currentStep:', currentStep);
    // console.log('CheckInFlow: tokenData:', tokenData);
    
    if (isLoading) {
      return (
        <div className="card">
          <div className="card-body">
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">
                <i className="fas fa-spinner fa-spin me-1"></i>
                Loading check-in process...
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (tokenError) {
      return (
          <div className="row main-container">
            <div className="col-sm-8 col-12">
              <div className="alert border-0" role="alert">
                <h4 className="alert-heading text-danger fs-4 mb-2"><i className="fas fa-exclamation-circle me-2"></i> Oops!</h4>
                <p>{tokenError}</p>
                <CheckInUrlHandler />
              </div>
              <div className="alert border-0">
                <p>If you continue to experience issues, please contact <a style={{ fontWeight: 500 }} href="/support-desk" target="_blank">Support Desk</a> for assistance.</p>
                <p className="small text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  Reference code: MIRA-PIN-{new Date().toISOString().substring(0, 10)}
                </p>
              </div>
            </div>
            <div className="col-sm-4 col-12 position-relative">
              <img src="/dist/assets/images/ankr_mira.svg" alt="Ankr Mira" className="img-fluid" style={{position:'absolute', right:0, bottom:0, zIndex:1, maxWidth:'400px', width:'90%'}} loading="lazy" />
              <img src="/dist/assets/images/ankr_mira_callout.svg" alt="Ankr Mira Callout" className="img-fluid" style={{position:'absolute', right:0, bottom:-30, zIndex:2, maxWidth:'250px', width:'70%'}} loading="lazy" />
              <img src="/dist/assets/images/ankr_mira_bg.svg" alt="Ankr Mira Bg" className="img-fluid" style={{position:'absolute', right:0, bottom:0, zIndex:0, maxWidth:'250px', width:'70%'}} loading="lazy" />
            </div>
          </div>
      );
    }

    const CurrentStepComponent = steps[currentStep].component;

    // Pass shortUrl to YearOfBirthVerification component
    if (currentStep === 0) {
      // console.log('CheckInFlow: Rendering YearOfBirthVerification component');
      return (
        <CurrentStepComponent
          shortUrl={shortUrl}
          onSuccess={handleStepComplete}
          checkInData={checkInData}
        />
      );
    }

    // Pass tokenData (decoded JWT payload) to subsequent steps
    // console.log('CheckInFlow: Rendering step component with tokenData:', tokenData);
    return (
      <CurrentStepComponent
        tokenData={tokenData}
        onSuccess={handleStepComplete}
        checkInData={checkInData}
        onFailure={() => setDeviceCheckFailed(true)} // Pass onFailure callback
      />
    );
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <div className="mb-4 d-flex justify-content-between">
              <div>
                <h1 className="card-title mb-2">Check In</h1>
                <p className="text-muted">Fast, easy and smart check-in experience!</p>
              </div>
              <p className="text-right">
                <Link to="/support-desk" className="text-muted">
                  <i className="far fa-life-ring me-1"></i><span className="d-none d-sm-block">Need Help?</span>
                </Link>
              </p>
            </div>

            {!isLoading && !tokenError && (
              <div className="step-progress-wrapper mb-4">
                <div className="step-progress">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step-item ${index === currentStep ? 'active' : ''} ${
                        index < currentStep ? 'completed' : ''
                      } ${index <= maxStepReached ? 'clickable' : ''} ${ // Only clickable if step is reached
                        index === 1 && deviceCheckFailed ? 'failed' : '' // Add 'failed' class to Device Check step if failed
                      }`}
                      onClick={() => index <= maxStepReached && goToStep(index)} // Only navigate if step is reached
                      style={{ cursor: index <= maxStepReached ? 'pointer' : 'default' }}
                      title={`Go to ${step.name}`}
                    >
                      <div className="step-item-icon">
                        {index === 1 && deviceCheckFailed ? ( // Check if it's the Device Check step and it failed
                          <i className="fa fa-lg fa-exclamation"></i> // Show exclamation icon
                        ) : index < currentStep ? ( // Otherwise, if step is completed
                          <i className="fas fa-check"></i> // Show checkmark
                        ) : ( // Otherwise, show step number
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <div className="step-item-label">{step.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
            {renderCurrentStep()}
            </div>
          </div>
          <div className="modal-footer justify-content-center">
            <div className="text-muted me-2">We speak your language: </div>
            <div className="language-buttons-container">
              <button className="btn btn-sm btn-outline-secondary"> English </button>
              <button className="btn btn-sm btn-outline-secondary"> Español </button>
              <button className="btn btn-sm btn-outline-secondary"> 㗂越 </button>
              <button className="btn btn-sm btn-outline-secondary"> Deutsch </button>
              <button className="btn btn-sm btn-outline-secondary"> हिन्दी </button>
              <button className="btn btn-sm btn-outline-secondary"> 普通话 </button>
              <button className="btn btn-sm btn-outline-secondary"> خليجي </button>
              {/* Add more language buttons as needed */}
            </div>
          </div>
    </div>
  );
};

export default CheckInFlow;
